import { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { MessageCircle, Send, User, Loader2 } from 'lucide-react';
import { useComments, useCreateComment } from '@/hooks/usePost';
import { useAuth } from '@/providers/AuthProvider';
import { formatCount } from '@/lib/formatters';
import { processAvatarUrl } from '@/lib/mediaUtils';
import { getPostUser } from '@/components/PostCard';
import CommentItem from '@/components/CommentItem';
import type { Post } from '@/types';

export interface CommentSectionProps {
  postUlid: string;
  /** Total comment count to display in header (from post engagement) */
  commentsCount?: number;
  onProfileClick?: (username: string) => void;
}

/** Tracks which comment we're replying to */
interface ReplyTarget {
  ulid: string;
  username: string;
}

export default function CommentSection({
  postUlid,
  commentsCount,
  onProfileClick,
}: CommentSectionProps) {
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);

  const commentsRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    data: commentsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useComments(postUlid);

  const createCommentMutation = useCreateComment();

  const comments = commentsData?.pages.flatMap((page) => page.comments) ?? [];

  // Stagger animation on initial load
  useEffect(() => {
    if (commentsRef.current && commentsRef.current.children.length > 0) {
      gsap.fromTo(
        commentsRef.current.children,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 0.3, stagger: 0.04, ease: 'power2.out' }
      );
    }
  }, [commentsData]);

  // Infinite scroll sentinel
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSubmit = () => {
    if (!newComment.trim()) return;

    createCommentMutation.mutate(
      {
        postUlid,
        body: newComment,
        parentId: replyTarget?.ulid,
      },
      {
        onSuccess: () => {
          setNewComment('');
          setReplyTarget(null);
        },
      }
    );
  };

  const handleReply = (comment: Post) => {
    const author = getPostUser(comment);
    setReplyTarget({
      ulid: comment.ulid ?? comment.id,
      username: author.username,
    });
    setNewComment(`@${author.username} `);
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyTarget(null);
    setNewComment('');
  };

  // User avatar
  const userRecord = user as Record<string, unknown> | null;
  const rawAvatar = (userRecord?.avatarUrl as string) || (userRecord?.avatar as string) || '';
  const avatarUrl = processAvatarUrl(rawAvatar) || '';

  const displayCount = commentsCount ?? comments.length;

  return (
    <div className="relative">
      {/* Header */}
      <h2 className="text-sm font-semibold text-[#A7B0C8] mb-3">
        Comments {displayCount > 0 ? `(${formatCount(displayCount)})` : ''}
      </h2>

      {/* Comments List */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={24} className="animate-spin text-[#A38BFF]" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-10 pb-24">
          <MessageCircle size={40} className="text-[#2A2F3A] mx-auto mb-3" />
          <p className="text-sm text-[#A7B0C8]">No comments yet. Be the first!</p>
        </div>
      ) : (
        <div ref={commentsRef} className="divide-y divide-white/[0.04] pb-20">
          {comments.filter((c) => {
                  const rec = c as Record<string, unknown>;
                  // Root comments have parentUlid === rootUlid (direct replies to post)
                  return rec.parentUlid === rec.rootUlid;
                })
            .map((comment) => (
            <CommentItem
              key={comment.ulid ?? comment.id}
              comment={comment}
              onProfileClick={onProfileClick}
              onReply={handleReply}
              allComments={comments}
              depth={0}
            />
          ))}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-1" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-[#A38BFF]" />
        </div>
      )}

      {/* Comment Input - sticky at bottom */}
      <div className="sticky bottom-0 left-0 right-0 bg-[#0B0D10] border-t border-white/[0.06] pt-3 pb-4 -mx-4 px-4">
        {/* Replying to indicator */}
        {replyTarget && (
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs text-[#A7B0C8]">
              Replying to <span className="text-[#A38BFF]">@{replyTarget.username}</span>
            </span>
            <button
              onClick={cancelReply}
              className="text-xs text-[#A7B0C8] hover:text-[#F5F7FF]"
            >
              Cancel
            </button>
          </div>
        )}
        <div className="flex items-center gap-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="You"
              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[#141821] flex items-center justify-center flex-shrink-0">
              <User size={16} className="text-[#A7B0C8]" />
            </div>
          )}
          <input
            ref={inputRef}
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder={replyTarget ? `Reply to @${replyTarget.username}...` : 'Add a comment...'}
            className="flex-1 h-10 bg-[#141821] rounded-full px-4 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/50 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all border border-white/[0.08]"
          />
          <button
            onClick={handleSubmit}
            disabled={!newComment.trim() || createCommentMutation.isPending}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
              newComment.trim() ? 'bg-[#A38BFF] text-[#0B0D10]' : 'bg-[#2A2F3A] text-[#A7B0C8]'
            }`}
          >
            {createCommentMutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
